/**
 * Pure vitals band helpers — shared by UI (optional) and StateSonifier.
 * Predicates match Proclaim BoardUi.leanClass / heartPulseClass exactly.
 */

/** Discrete tilt band (narrative id, no CSS prefix). */
export type LeanBand =
  | "truth-strong"
  | "truth"
  | "contested"
  | "lie"
  | "lie-strong";

/** Integrity heart-pulse band. */
export type IntegrityBand = "rest" | "alert" | "urgent" | "critical";

export const LEAN_BANDS: readonly LeanBand[] = [
  "truth-strong",
  "truth",
  "contested",
  "lie",
  "lie-strong",
] as const;

export const INTEGRITY_BANDS: readonly IntegrityBand[] = [
  "rest",
  "alert",
  "urgent",
  "critical",
] as const;

/** Exact BoardUi.leanClass predicates → band id. */
export function leanBand(tilt: number): LeanBand {
  if (tilt <= -40) return "truth-strong";
  if (tilt < -8) return "truth";
  if (tilt <= 8) return "contested";
  if (tilt < 40) return "lie";
  return "lie-strong";
}

/** CSS class matching BoardUi (`lean-truth-strong`, …). */
export function leanCssClass(tilt: number): string {
  return `lean-${leanBand(tilt)}`;
}

/**
 * Integrity bands — calm for most of the fight; “fast” only near loss.
 *
 * rest     ≥ 0.42  healthy / good (faint slow pulse)
 * alert    ≥ 0.24  pressure building
 * urgent   ≥ 0.11  real trouble
 * critical < 0.11  near loss
 */
export function integrityBandFromFrac(frac: number): IntegrityBand {
  const f = Math.max(0, Math.min(1, frac));
  if (f >= 0.42) return "rest";
  if (f >= 0.24) return "alert";
  if (f >= 0.11) return "urgent";
  return "critical";
}

/** CSS class matching BoardUi (`pulse-rest`, …). */
export function integrityPulseCssClass(frac: number): string {
  return `pulse-${integrityBandFromFrac(frac)}`;
}

/** Lower edge of band (minimum frac to be in this band or healthier raw classification). */
export function integrityBandLowerEdge(band: IntegrityBand): number {
  switch (band) {
    case "rest":
      return 0.42;
    case "alert":
      return 0.24;
    case "urgent":
      return 0.11;
    case "critical":
      return 0;
  }
}

const BAND_RANK: Record<IntegrityBand, number> = {
  rest: 0,
  alert: 1,
  urgent: 2,
  critical: 3,
};

/**
 * Sticky integrity band with hysteresis so pulse/drone do not chatter at thresholds.
 * HYST = 0.03 relative to band lower edges.
 */
export function integrityBandWithHysteresis(
  lastBand: IntegrityBand,
  frac: number,
  hyst = 0.03,
): IntegrityBand {
  const f = Math.max(0, Math.min(1, frac));
  const raw = integrityBandFromFrac(f);
  if (raw === lastBand) return lastBand;

  const lastRank = BAND_RANK[lastBand];
  const rawRank = BAND_RANK[raw];

  if (rawRank > lastRank) {
    // Getting worse: must cross below last band's lower edge − hyst
    const boundary = integrityBandLowerEdge(lastBand);
    if (f < boundary - hyst) return raw;
    return lastBand;
  }

  // Getting better: must cross above raw band's lower edge + hyst
  const boundary = integrityBandLowerEdge(raw);
  if (f >= boundary + hyst) return raw;
  return lastBand;
}

/**
 * Heart pulse tempo by integrity band (discrete reference / UI fallback).
 * Prefer {@link integrityPulseBpmFromFrac} for audio — continuous, no steps.
 */
export function integrityPulseBpm(band: IntegrityBand): number {
  switch (band) {
    case "rest":
      return 50;
    case "alert":
      return 58;
    case "urgent":
      return 76;
    case "critical":
      return 96;
  }
}

/**
 * Continuous heart tempo from integrity fraction (no band steps).
 * Stays near a calm rest (~50 BPM, slightly under a typical 60–70 adult rest)
 * until integrity is actually low, then eases upward toward ~100 BPM.
 *
 * Approx: full→50 · half→~53 · 25%→~66 · 10%→~84 · 0→100
 */
export function integrityPulseBpmFromFrac(frac: number): number {
  const f = Math.max(0, Math.min(1, frac));
  const stress = 1 - f;
  // Strong ease-in (cubic): early damage barely moves tempo.
  const t = stress * stress * stress * (0.35 + 0.65 * stress);
  return 50 + t * 50; // 50 … 100
}

/**
 * Continuous pulse gain. Rest is faint-but-present (readable under the bed);
 * rises as integrity falls. Absolute levels account for state-bus ~0.5.
 */
export function integrityPulseGainFromFrac(frac: number): number {
  const f = Math.max(0, Math.min(1, frac));
  const stress = 1 - f;
  const t = stress * stress; // quieter early, fuller near critical
  // ~0.32 at full integrity (faint thump), ~0.58 near zero
  return 0.32 + t * 0.26;
}

/** Peak one-shot gain for pulse by band (legacy; prefer FromFrac). */
export function integrityPulseGain(band: IntegrityBand): number {
  switch (band) {
    case "rest":
      return 0.32;
    case "alert":
      return 0.4;
    case "urgent":
      return 0.5;
    case "critical":
      return 0.58;
  }
}

/** CSS animation period (seconds) matching continuous audio BPM. */
export function integrityPulsePeriodSec(frac: number): number {
  return 60 / integrityPulseBpmFromFrac(frac);
}
